<template>
  <transition name="promotion-slidein">
    <div
      v-if="isActive"
      class="promotion-slidein"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="`promotion-slidein-heading-${uuid}`"
      :aria-describedby="`promotion-slidein-content-${uuid}`"
      @keydown.esc="close"
    >
      <div class="spacer-horizontal">
        <a-focus-trap initial-focus=".promotion-slidein__button-close">
          <div class="promotion-slidein__inner spacer-horizontal__inset-narrow">
            <article>
              <header v-if="$slots.heading" class="promotion-slidein__header">
                <div class="promotion-slidein__logos">
                  <slot name="logos" />
                </div>
                <h1
                  :id="`promotion-slidein-heading-${uuid}`"
                  class="promotion-slidein__heading"
                >
                  <slot name="heading" />
                </h1>
              </header>
              <div
                v-if="$slots.content"
                :id="`promotion-slidein-content-${uuid}`"
                class="promotion-slidein__content"
              >
                <slot name="content" />
              </div>
              <footer v-if="$slots.footer" class="promotion-slidein__footer">
                <button
                  v-if="showAgainAtMin && showAgainButtonText"
                  class="promotion-slidein__button styled-button"
                  @click="closeTemporarily"
                >
                  {{ showAgainButtonText }}
                </button>
                <slot name="footer" />
                <div class="promotion-slidein__disclaimer">
                  <slot name="disclaimer" />
                </div>
              </footer>
            </article>
            <div class="promotion-slidein__button-close-wrapper">
              <bpb-flag label-path="dialog.close" direction="top">
                <button
                  class="promotion-slidein__button-close"
                  @click="closeForever"
                >
                  <bpb-icon
                    class="promotion-slidein__icon-close"
                    icon="i-close"
                    label-path="dialog.close"
                  ></bpb-icon>
                </button>
              </bpb-flag>
            </div>
          </div>
        </a-focus-trap>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  provide() {
    const promotionSlideinContext = {};

    promotionSlideinContext.closeForever = this.closeForever;
    promotionSlideinContext.close = this.close;

    return {
      promotionSlideinContext: promotionSlideinContext,
    };
  },
  props: {
    delayForSec: {
      type: Number,
      default: 0,
    },
    storeKey: {
      type: String,
      required: true,
    },
    chanceOneIn: {
      type: Number,
      default: 1,
    },
    showAgainAtMin: {
      type: Number,
      default: 0,
    },
    showAgainButtonText: {
      type: String,
    },
    chanceIsBoundToClient: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      uuid: this.$uuid.v1(),
      isActive: false,
      showAgain: false,
      showAgainTime: false,
      deactivated: false,
      prevFocusedEl: undefined,
    };
  },
  computed: {
    items() {
      return this.$store.getters['promotionSlidein/items'];
    },
    item() {
      return this.items[this.storeKey];
    },
    showByChance() {
      // IF chanceIsBoundToClient - check whether chance has already been rolled for this client
      if (this.chanceIsBoundToClient && this.item) return !this.isDeactivated;
      if (!this.chanceOneIn && this.chanceOneIn !== 0) return true;
      const chance = Math.abs(this.chanceOneIn);
      return Math.floor(Math.random() * chance) + 1 === chance;
    },
    hasShowAgainActivated() {
      return !!this.item?.showAgainAtMin;
    },
    isDeactivated() {
      return !!this.item?.deactivated;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.show();
    });
  },
  methods: {
    close() {
      this.rememberShow();
      this.deactivate();
    },
    closeForever() {
      this.showAgain = false;
      this.deactivated = true;
      this.close();
    },
    closeTemporarily() {
      this.showAgain = true;
      this.close();
      this.show();
    },
    show() {
      if (this.isDeactivated || this.deactivated) {
        return;
      }

      if (this.hasShowAgainActivated || this.showAgain) {
        const showTime = this.showAgainTime || this.item.showAgainAtMin;
        const interval = setInterval(() => {
          if (showTime < Date.now()) {
            this.activate();
            clearInterval(interval);
            return;
          }
        }, 60000); // Check every minute.
        return;
      }

      // IF chanceIsBoundToClient - roll chance and permanently deactivate if this.showByChance is false
      if (this.chanceIsBoundToClient) {
        this.$store.dispatch('promotionSlidein/add', {
          item: {
            [this.storeKey]: {
              showAgainAtMin: false,
              deactivated: !this.showByChance,
            },
          },
        });
      }
      if (!this.showByChance) return;

      setTimeout(() => {
        this.activate();
      }, this.delayForSec * 1000);
    },
    rememberShow() {
      this.showAgainTime =
        this.showAgain && this.showAgainAtMin
          ? Date.now() + this.showAgainAtMin * 60000
          : false; // 60000 - convert to ms

      this.$store.dispatch('promotionSlidein/add', {
        item: {
          [this.storeKey]: {
            showAgainAtMin: this.showAgainTime,
            deactivated: this.deactivated,
          },
        },
      });
    },
    activate() {
      this.prevFocusedEl = document.activeElement;
      this.$nextTick(() => {
        this.isActive = true;
      });
    },
    deactivate() {
      this.isActive = false;
      this.prevFocusedEl.focus();
      this.prevFocusedEl = undefined;
    },
  },
};
</script>
